/**
 * Orders — Services / Marketplace tabs.
 * One "Orders" destination under Finance, split by domain.
 */
import { useMemo, useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/common';
import { usePermissions } from '../../hooks/usePermissions';
import OrdersListPage from './OrdersListPage';
import CreateOrderDialog from './CreateOrderDialog';
import { MarketplaceOrdersListPage } from '../marketplace';

const OrdersTabsPage = () => {
  const { t } = useTranslation();
  const { hasPermission, isSuperUser } = usePermissions();
  const su = isSuperUser();

  const tabs = useMemo(() => {
    const list: { key: 'services' | 'marketplace'; label: string }[] = [];
    if (su || hasPermission('view_orders')) list.push({ key: 'services', label: t('orders.tabs.services') });
    if (su || hasPermission('view_marketplace')) list.push({ key: 'marketplace', label: t('orders.tabs.marketplace') });
    return list;
  }, [su, hasPermission, t]);

  // The active tab lives in the URL (`/orders?tab=marketplace`) so pages can link
  // straight back to the tab an order belongs to. Falls back to the first tab the
  // user is allowed to see.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = tabs.findIndex((item) => item.key === searchParams.get('tab'));
  const tab = requested >= 0 ? requested : 0;
  const active = tabs[tab]?.key;

  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = active === 'services' && (su || hasPermission('create_orders'));

  const handleTabChange = (_e: React.SyntheticEvent, value: number) => {
    setSearchParams({ tab: tabs[value].key }, { replace: true });
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), path: '/' }, { label: t('orders.title') }]}
        actionLabel={canCreate ? t('serviceOrders.create.button') : undefined}
        onAction={canCreate ? () => setCreateOpen(true) : undefined}
      />

      {/* Mounted only while open so every opening starts with a clean form */}
      {createOpen && (
        <CreateOrderDialog
          open
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => navigate(`/orders/${id}`)}
        />
      )}

      {tabs.length > 1 && (
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      )}

      {active === 'services' && <OrdersListPage embedded />}
      {active === 'marketplace' && <MarketplaceOrdersListPage embedded />}
    </Box>
  );
};

export default OrdersTabsPage;
